import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, concatMap, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';
import { ConfirmDialogModel, ConfirmDialogService } from '@ui';

import { ProjectService } from '../../services/project.service';
import { ProjectActions } from '../state/project/project.actions';
import { AppState } from '../app.state';
import { projectFeature } from '../state/project/project.feature';

export const getProject = createEffect(
  (actions$ = inject(Actions), projectService = inject(ProjectService)) => {
    return actions$.pipe(
      ofType(ProjectActions.getProject),
      switchMap(({ projectId }) => {
        return projectService.findOne(projectId).pipe(
          map((project) => ProjectActions.getProjectSuccess({ project })),
          catchError(() => of(ProjectActions.getProjectFail({ projectId }))),
        );
      }),
    );
  },
  { functional: true },
);

export const beforeLoadProjects = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ProjectActions.beforeLoadProjects),
      map(({ filters }) => ProjectActions.loadProjects({ filters })),
    );
  },
  { functional: true },
);

export const loadProjects = createEffect(
  (
    actions$ = inject(Actions),
    projectService = inject(ProjectService),
    store$ = inject<Store<AppState>>(Store),
  ) => {
    return actions$.pipe(
      ofType(ProjectActions.loadProjects),
      concatLatestFrom(() => [
        store$.select(projectFeature.selectLatestTs),
        store$.select(projectFeature.selectIsLastPage),
      ]),
      switchMap(([{ filters }, latestTs, isLastPage]) => {
        if (isLastPage) {
          return of(ProjectActions.doNothing());
        }

        return projectService.findAll(filters, latestTs).pipe(
          map((projects) =>
            ProjectActions.loadProjectsSuccess({ projects, filters }),
          ),
          catchError(() => of(ProjectActions.loadProjectsFail())),
        );
      }),
    );
  },
  { functional: true },
);

export const createProject = createEffect(
  (actions$ = inject(Actions), projectService = inject(ProjectService)) => {
    return actions$.pipe(
      ofType(ProjectActions.createProject),
      exhaustMap(({ payload }) => {
        return projectService.create(payload).pipe(
          map((project) => ProjectActions.createProjectSuccess({ project })),
          catchError(() => of(ProjectActions.createProjectFail())),
        );
      }),
    );
  },
  { functional: true },
);

export const updateProject = createEffect(
  (actions$ = inject(Actions), projectService = inject(ProjectService)) => {
    return actions$.pipe(
      ofType(ProjectActions.updateProject),
      exhaustMap(({ projectId, payload }) => {
        return projectService.update(projectId, payload).pipe(
          map((project) => ProjectActions.updateProjectSuccess({ project })),
          catchError(() => of(ProjectActions.updateProjectFail())),
        );
      }),
    );
  },
  { functional: true },
);

export const closeProject = createEffect(
  (actions$ = inject(Actions), projectService = inject(ProjectService)) => {
    return actions$.pipe(
      ofType(ProjectActions.closeProject),
      mergeMap(({ projectId, payload }) => {
        return projectService.close(projectId, payload).pipe(
          map((project) => ProjectActions.closeProjectSuccess({ project })),
          catchError(() => of(ProjectActions.closeProjectFail({ projectId }))),
        );
      }),
    );
  },
  { functional: true },
);

export const requestDeleteProject = createEffect(
  (
    actions$ = inject(Actions),
    confirmDialogService = inject(ConfirmDialogService),
  ) => {
    return actions$.pipe(
      ofType(ProjectActions.requestDeleteProject),
      concatMap(({ project }) => {
        return confirmDialogService
          .open({
            type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
            title: 'PROJECT_LIST.DELETE_CONFIRM.TITLE',
            msg: 'PROJECT_LIST.DELETE_CONFIRM.MESSAGE',
            params: {
              projectName: project.name,
            },
            confirm: 'PROJECT_LIST.ACTIONS.DELETE',
            cancel: 'COMMON.CANCEL',
            disableClose: true,
          })
          .pipe(
            map((result) =>
              result?.selection === ConfirmDialogModel.ConfirmResult.OK
                ? ProjectActions.deleteProject({ projectId: project._id })
                : ProjectActions.requestDeleteProjectCancel(),
            ),
          );
      }),
    );
  },
  { functional: true },
);

export const deleteProject = createEffect(
  (actions$ = inject(Actions), projectService = inject(ProjectService)) => {
    return actions$.pipe(
      ofType(ProjectActions.deleteProject),
      mergeMap(({ projectId }) => {
        return projectService.remove(projectId).pipe(
          map(() => ProjectActions.deleteProjectSuccess({ projectId })),
          catchError(() => of(ProjectActions.deleteProjectFail({ projectId }))),
        );
      }),
    );
  },
  { functional: true },
);
